import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { WebhookEndpoint, WebhookEventLog } from '../../../types/organization.types'

interface WebhooksState {
  endpoints: WebhookEndpoint[]
  events: WebhookEventLog[]
  loading: boolean
  error: string | null
  selectedEndpoint: WebhookEndpoint | null
  creatingEndpoint: boolean
  updatingEndpointId: string | null
  deletingEndpointId: string | null
  eventsLoading: boolean
  retryingEventId: string | null
}

const initialState: WebhooksState = {
  endpoints: [],
  events: [],
  loading: false,
  error: null,
  selectedEndpoint: null,
  creatingEndpoint: false,
  updatingEndpointId: null,
  deletingEndpointId: null,
  eventsLoading: false,
  retryingEventId: null,
}

const webhooksSlice = createSlice({
  name: 'organizationWebhooks',
  initialState,
  reducers: {
    // Fetch webhook endpoints
    fetchEndpointsStart(state) {
      state.loading = true
      state.error = null
    },
    fetchEndpointsSuccess(state, action: PayloadAction<WebhookEndpoint[]>) {
      state.loading = false
      state.endpoints = action.payload
      state.error = null
    },
    fetchEndpointsFailure(state, action: PayloadAction<string>) {
      state.loading = false
      state.error = action.payload
    },

    // Create webhook endpoint
    createEndpointStart(state) {
      state.creatingEndpoint = true
      state.error = null
    },
    createEndpointSuccess(state, action: PayloadAction<WebhookEndpoint>) {
      state.creatingEndpoint = false
      state.endpoints.push(action.payload)
      state.error = null
    },
    createEndpointFailure(state, action: PayloadAction<string>) {
      state.creatingEndpoint = false
      state.error = action.payload
    },

    // Update webhook endpoint
    updateEndpointStart(state, action: PayloadAction<string>) {
      state.updatingEndpointId = action.payload
      state.error = null
    },
    updateEndpointSuccess(state, action: PayloadAction<WebhookEndpoint>) {
      state.updatingEndpointId = null
      const index = state.endpoints.findIndex((e) => e.id === action.payload.id)
      if (index !== -1) {
        state.endpoints[index] = action.payload
      }
      state.error = null
    },
    updateEndpointFailure(state, action: PayloadAction<string>) {
      state.updatingEndpointId = null
      state.error = action.payload
    },

    // Delete webhook endpoint
    deleteEndpointStart(state, action: PayloadAction<string>) {
      state.deletingEndpointId = action.payload
      state.error = null
    },
    deleteEndpointSuccess(state, action: PayloadAction<string>) {
      state.deletingEndpointId = null
      state.endpoints = state.endpoints.filter((e) => e.id !== action.payload)
      state.error = null
    },
    deleteEndpointFailure(state, action: PayloadAction<string>) {
      state.deletingEndpointId = null
      state.error = action.payload
    },

    // Fetch webhook events
    fetchEventsStart(state) {
      state.eventsLoading = true
      state.error = null
    },
    fetchEventsSuccess(state, action: PayloadAction<WebhookEventLog[]>) {
      state.eventsLoading = false
      state.events = action.payload
      state.error = null
    },
    fetchEventsFailure(state, action: PayloadAction<string>) {
      state.eventsLoading = false
      state.error = action.payload
    },

    // Retry webhook event
    retryEventStart(state, action: PayloadAction<string>) {
      state.retryingEventId = action.payload
      state.error = null
    },
    retryEventSuccess(state, action: PayloadAction<WebhookEventLog>) {
      state.retryingEventId = null
      const index = state.events.findIndex((e) => e.id === action.payload.id)
      if (index !== -1) {
        state.events[index] = action.payload
      }
      state.error = null
    },
    retryEventFailure(state, action: PayloadAction<string>) {
      state.retryingEventId = null
      state.error = action.payload
    },

    // Select endpoint
    selectEndpoint(state, action: PayloadAction<WebhookEndpoint | null>) {
      state.selectedEndpoint = action.payload
    },

    // Clear error
    clearError(state) {
      state.error = null
    },

    // Reset state
    resetWebhooksState: () => initialState,
  },
})

export const {
  fetchEndpointsStart,
  fetchEndpointsSuccess,
  fetchEndpointsFailure,
  createEndpointStart,
  createEndpointSuccess,
  createEndpointFailure,
  updateEndpointStart,
  updateEndpointSuccess,
  updateEndpointFailure,
  deleteEndpointStart,
  deleteEndpointSuccess,
  deleteEndpointFailure,
  fetchEventsStart,
  fetchEventsSuccess,
  fetchEventsFailure,
  retryEventStart,
  retryEventSuccess,
  retryEventFailure,
  selectEndpoint,
  clearError,
  resetWebhooksState,
} = webhooksSlice.actions

export default webhooksSlice.reducer
